import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
// Backend returns Page<StorageOut> = {items, total, limit, offset}; unwrap to items[].
export function useStorages() {
    return useQuery({
        queryKey: ['storages'],
        queryFn: async () => (await api('/storages')).items,
    });
}
export function useCreateStorage() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (input) => api('/storages', { method: 'POST', body: input }),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['storages'] }),
    });
}
export function useUpdateStorage(id) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (input) => api(`/storages/${id}`, { method: 'PATCH', body: input }),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['storages'] }),
    });
}
export function useDeleteStorage() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id) => api(`/storages/${id}`, { method: 'DELETE' }),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['storages'] }),
    });
}
// Existing-record test: HeadBucket + PutObject + DeleteObject under
// __healthcheck/ using creds resolved from the SecretsProvider.
export function useTestStorage() {
    return useMutation({
        mutationFn: (id) => api(`/storages/${id}/test`, { method: 'POST' }),
    });
}
// Auto-test as a query: fires on mount of the detail sheet so the header pill
// reflects live state without the user clicking a button. Same pattern as
// useExistingConnectionTest in useDatabases.
export function useStorageConnectionTest(id) {
    return useQuery({
        queryKey: ['storage-test', id],
        queryFn: () => api(`/storages/${id}/test`, { method: 'POST' }),
        enabled: !!id,
        retry: false,
        gcTime: 60_000,
        staleTime: 0,
    });
}
export function useStorageInspect(id) {
    return useQuery({
        queryKey: ['storage-inspect', id],
        queryFn: () => api(`/storages/${id}/inspect`),
        enabled: !!id,
        retry: false,
        gcTime: 60_000,
        staleTime: 0,
    });
}
export function useStorageUploads(id, limit = 10) {
    return useQuery({
        queryKey: ['storage-uploads', id, limit],
        queryFn: () => api(`/storages/${id}/uploads?limit=${limit}`),
        enabled: !!id,
    });
}
// Form-time test: caller supplies plain creds (used by the create/edit form
// before save).
export function useTestStorageForm() {
    return useMutation({
        mutationFn: (input) => api(`/storages/test`, { method: 'POST', body: input }),
    });
}
